'use client';

import { ComponentProps, useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import { cn } from '@/lib';
import { PrefetchLink, Text } from '@/ui';
import { imageMetaConverter } from '@lib/server';
import type { APIImageType } from '@types';
import clsx from 'clsx';
import { Vibrant } from 'node-vibrant/browser';
import tinycolor from 'tinycolor2';

export interface LogoProps extends ComponentProps<typeof PrefetchLink> {
  logo: APIImageType | APIImageType[];
  /* invert the logo if the background/foreground is not accessible e.g. if this component is in the navigation and not scrolled, the logo will be on the hero banner and should be accessible anyway */
  checkAccessibility?: boolean;
  siteName: string;
}

export function Logo({ logo, siteName, className, href = '/', checkAccessibility = true, ...rest }: LogoProps) {
  const image = imageMetaConverter(logo, {}, { includeDimensions: true });
  const [isAccessible, setIsAccessible] = useState(true);
  const [adjustment, setAdjustment] = useState<'darken' | 'lighten'>('darken');
  const logoRef = useRef<HTMLDivElement | null>(null);

  const style = image?.width
    ? {
        aspectRatio: (image.width as number) / (image.height as number),
      }
    : {};
  const logoClassName = clsx('relative flex h-10 max-w-32 items-center sm:max-w-64', className);

  // Function to get the main color of the logo
  const getMainColor = async (imageUrl: string) => {
    const palette = await Vibrant.from(imageUrl).getPalette();
    return palette.Vibrant?.hex || '#ffffff';
  };

  const getComputedBackgroundColor = (element: HTMLElement | null): string => {
    while (element && element !== document.body) {
      const bgColor = window.getComputedStyle(element).backgroundColor;
      if (bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        return tinycolor(bgColor).toHexString();
      }
      element = element.parentElement;
    }

    return '#ffffff';
  };

  useEffect(() => {
    const checkImageAccessibility = async () => {
      if (logoRef.current) {
        const backgroundColor = getComputedBackgroundColor(logoRef.current.parentElement);
        const mainColor = await getMainColor(image?.src || '');

        const isAccessible = tinycolor.readability(backgroundColor, mainColor) > 2;

        setIsAccessible(isAccessible);
        setAdjustment(tinycolor(backgroundColor).isDark() ? 'lighten' : 'darken');
      }
    };

    if (image) {
      checkImageAccessibility();
    }
  }, [image]);

  return image ? (
    <div ref={logoRef}>
      <PrefetchLink style={style} className={logoClassName} href={href} {...rest}>
        <Image
          {...image}
          style={style}
          alt={siteName}
          sizes={'150px'}
          className={cn(
            {
              'brightness-50 saturate-150': !isAccessible && checkAccessibility && adjustment === 'darken',
              'brightness-150 saturate-150': !isAccessible && checkAccessibility && adjustment === 'lighten',
            },
            'grow object-contain',
          )}
        />
      </PrefetchLink>
    </div>
  ) : (
    <PrefetchLink style={style} className={logoClassName} href={href} {...rest}>
      <Text element="span" elementStyle="h4">
        {siteName}
      </Text>
    </PrefetchLink>
  );
}
